// src/tattoo/state.js
import { proxy } from "valtio";
import toast from "react-hot-toast";
import { buildFlattenedUV, buildDesignExports } from "./pipeline.js";
import { makeZip } from "./lib/zip.js";
import {
  compressToEncodedURIComponent,
  decompressFromEncodedURIComponent,
} from "lz-string";
import { commentsStore } from "./commentsState";
import { applyProjectReactions, exportCountsOnly } from "./reactionsState";
import { removeWhiteBackgroundPro, featherAndBleedAlpha } from "./imageUtils";
import { upsertRecentProject } from "./projectsIndex";
import { guidePaintRef } from "./GuidePaint";
import { isIOS, downscaleImageFile } from "./lib/imagePrep";
import { renderTextLayer, TEXT_DEFAULTS } from "./lib/textLayer";
import { mergeLayersToDataURL } from "./pipeline.js";
// or "./utils", "./ids", wherever it actually lives

/* ---------------- internals ---------------- */
function bump() {
  tattoo._rev = (tattoo._rev || 0) + 1;

  tattoo._dirty = true; // autosave watches this
}

export function forceBump() {
  bump();
}

function newId() {
  return Math.random().toString(36).slice(2);
}
function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}

function normalizeTransform(t = {}) {
  const out = { tx: 0.5, ty: 0.5, scale: 0.5, rot: 0 };

  if (Number.isFinite(t.tx)) out.tx = clamp01(t.tx);
  if (Number.isFinite(t.ty)) out.ty = clamp01(t.ty);
  if (Number.isFinite(t.scale)) out.scale = t.scale;
  if (Number.isFinite(t.rot)) out.rot = t.rot;

  // ✅ preserve free-transform axes if provided
  if (Number.isFinite(t.scaleX)) out.scaleX = t.scaleX;
  if (Number.isFinite(t.scaleY)) out.scaleY = t.scaleY;

  // 🔹 new: skew
  if (Number.isFinite(t.skewX)) out.skewX = t.skewX;
  if (Number.isFinite(t.skewY)) out.skewY = t.skewY;

  // legacy support
  if (t.offset && Number.isFinite(t.offset.x)) out.tx = clamp01(t.offset.x);
  if (t.offset && Number.isFinite(t.offset.y)) out.ty = clamp01(t.offset.y);
  if (Number.isFinite(t.rotate)) out.rot = t.rotate;

  return out;
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

async function readFileAsDataURL(file) {
  return await new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onerror = () => rej(fr.error);
    fr.onload = () => res(fr.result);
    fr.readAsDataURL(file);
  });
}
async function blobUrlToDataURL(url) {
  const resp = await fetch(url);
  const blob = await resp.blob();
  return await readFileAsDataURL(blob);
}

/* ---------------- Comments compat shim ---------------- */
function extractCommentsFromData(data) {
  try {
    if (data && data.comments && typeof data.comments === "object") {
      return data.comments;
    }
    if (Array.isArray(data?.layers)) {
      const withComments = data.layers.find(
        (L) => L && L.comments && typeof L.comments === "object"
      );
      if (withComments) return withComments.comments;
    }
  } catch {}
  return {};
}

// drag release helper //

export function ttEndDecalDrag({ forceHigh = true } = {}) {
  const d = window.__tt_decal_drag;
  if (d) {
    d.active = false;
    d.layerId = null;
    d.hitWorld = null;
    d.tx = null;
    d.ty = null;
    d.url = null;
  }
  window.__tt_decal_drag_active = false;

  // snap back to crisp once, if you want
  if (forceHigh) {
    tattoo.renderQuality = "high";
    tattoo.renderQualityTick = (tattoo.renderQualityTick || 0) + 1;
  }
}

/* ---------------- Layer shape & defaults ---------------- */
export function layerDefaults(partial = {}) {
  const opacity =
    typeof partial.opacity === "number"
      ? Math.max(0, Math.min(1, partial.opacity))
      : 1.0;

  const blend = partial.blend || "skin"; // "skin" = legacy dark-ink mode

  return {
    id: newId(),
    name: "layer",
    url: "",
    visible: true,
    opacity,
    blend, // "skin" | "multiply" | "white" | "normal" etc
    edgeClip: tattoo.edgeClip ?? 0.15,
    transform: {
      tx: 0.5,
      ty: 0.5,
      scale: 0.5,
      rot: 0,
      skewX: 0,
      skewY: 0,
      ...(partial.transform || {}),
    },
    partKeys: ["torso"],
    sex: undefined,
    part: undefined,
    // inside layerDefaults(...)
    anchor: partial.anchor ?? null,
    suggestion: false,
    suggestedBy: null,
    createdAt: partial.createdAt ?? Date.now(),
    ...partial,
  };
}

// Fast path: during drag, mutate the existing layer object in-place
// (avoids rebuilding tattoo.layers array 60 times/sec)
export function setLayerTransformFast(id, patch) {
  const i = tattoo.layers.findIndex((l) => l.id === id);
  if (i < 0 || tattoo.layers[i]?.locked) return;

  const cur = tattoo.layers[i];
  cur.transform = normalizeTransform({
    ...(cur.transform || {}),
    ...(patch || {}),
  });

  // IMPORTANT: do NOT bump here
}

export function fastSetLayerTxTy(layerId, tx, ty) {
  const layers = tattoo.layers || [];
  const i = layers.findIndex((l) => l.id === layerId);
  if (i < 0) return;
  const L = layers[i];
  if (L.locked) return;

  // ensure transform exists
  if (!L.transform) L.transform = { tx: 0.5, ty: 0.5, scale: 0.5, rot: 0 };

  // ✅ mutate in place (NO tattoo.layers = newArray)
  L.transform.tx = tx;
  L.transform.ty = ty;
}

/* ---------------- Context helpers ---------------- */
function _currentContext() {
  const sex = tattoo.sex || "female";
  const view = tattoo.viewMode || (sex === "female" ? "fullbody" : "parts");
  const part =
    view === "fullbody"
      ? "fullbody"
      : Object.keys(tattoo.parts).find((k) => tattoo.parts[k]) || "torso";
  return { sex, part };
}
function _matchesContext(L, sex, part) {
  const sexOk = L.sex ? L.sex === sex : true;
  const partOk = L.part
    ? L.part === part
    : Array.isArray(L.partKeys)
    ? L.partKeys.includes(part)
    : true;
  return sexOk && partOk;
}

// Convenience: current-context filtered view of layers
export function getLayersForCurrentContext() {
  const { sex, part } = _currentContext();
  return (tattoo.layers || []).filter((L) => _matchesContext(L, sex, part));
}

const DEFAULT_APPEARANCE_BY_SEX = {
  female: { top: "shirt", bottom: "pants", hair: "none" },
  male: { top: "tank", bottom: "jeans", hair: "none" },
};

function getDefaultAppearance(sex) {
  return DEFAULT_APPEARANCE_BY_SEX[sex === "male" ? "male" : "female"];
}

// Only fill missing keys; never clobber existing values (and never drop extra keys)
function ensureAppearanceDefaults(sex) {
  const def = getDefaultAppearance(sex);
  const cur = tattoo.appearance || {};

  tattoo.appearance = {
    ...cur, // ✅ keep hairColor*
    top: cur.top ?? def.top,
    bottom: cur.bottom ?? def.bottom,
    hair: cur.hair ?? def.hair,
    hairColorMode: cur.hairColorMode ?? "preset",
    hairColorPreset: cur.hairColorPreset ?? "brown",
    hairColorCustom: cur.hairColorCustom ?? "#3b2a1a",
  };
}

function dataURLToBlob(dataURL) {
  const [head, body] = String(dataURL).split(",");
  const mime = /data:(.*?);base64/.exec(head)?.[1] || "image/png";
  const bin = atob(body);
  const len = bin.length;
  const arr = new Uint8Array(len);
  for (let i = 0; i < len; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

function dataURLToFile(dataURL, filename = "merged.png") {
  const blob = dataURLToBlob(dataURL);
  return new File([blob], filename, { type: blob.type });
}

/* ---------------- App state ---------------- */
export const tattoo = proxy({
  // global
  uvImage: null,
  skinColor: "#d19771",
  overlayOpacity: 0.85,
  edgeClip: 0.8,
  draggingLayerId: null, // <- new
  poseMorphNames: [], // auto-discovered list for UI
  poseWeights: {}, // { "POSE__Neutral": 1, "POSE__Hip Pop": 0, ... }
  decalMode: null, // null | "place" | "drag"
  // 🔹 project identity
  projectId: null,
  projectName: undefined,
  renderQuality: "high", // "high" | "drag"
  renderQualityTick: 0, // bump to force rebuild

  // view + context
  sex: "female", // "female" | "male"
  viewMode: "fullbody", // "parts" | "fullbody"
  parts: { torso: true, legs: false },

  // body morphs (captured in save/share)
  morphs: {
    height: 0,
    weight: 0,
    muscular: 0,
    chest: 0,
    waist: 0,
    hips: 0,
  },

  // model URLs
  modelUrls: {
    female: {
      full: "/tattoo/models/Female.glb",
      torso: "/tattoo/models/Female.glb",
      legs: "/tattoo/models/Female.glb",
    },
    male: {
      full: "/tattoo/models/Male.glb",
      torso: "/tattoo/models/Male.glb",
      legs: "/tattoo/models/Male.glb",
    },
  },

  // inside tattoo proxy
  appearance: {
    top: "none",
    bottom: "none",
    hair: "none",
    hairColorMode: "preset", // "preset" | "custom" | "skin"
    hairColorPreset: "dark", // id of preset
    hairColorCustom: "#2b1a12", // hex

    // ✅ NEW
    topColorMode: "preset", // "preset" | "custom"
    topColorPreset: "black", // id
    topColorCustom: "#111111", // hex

    bottomColorMode: "preset",
    bottomColorPreset: "denim",
    bottomColorCustom: "#222222",
  },

  // export / guide overlays
  exportGuideUrl: "/tattoo/uv/female_full_uv.png",
  exportGuideAlpha: 0.35,
  exportIncludeGuide: true,

  // layers + current edit id
  layers: [],
  editingLayerId: null,

  // UV guides
  uvGuides: {
    fullbody_female: "/tattoo/uv/female_full_uv.png",
    fullbody_male: "/tattoo/uv/male_full_uv.png",
  },

  // UV rects (0..1 atlas coords)
  uvRects: {
    fullbody_female: { x: 0, y: 0, w: 1, h: 1 },
    fullbody_male: { x: 0, y: 0, w: 1, h: 1 },
  },

  // layout / stage
  leftOpen: false,
  stage: "design", // "design" | "preview"

  // revision tick
  _rev: 0,
  _id: 1,
});

tattoo.ui = {
  mergeConfirm: {
    open: false,
    layerId: null,
    partKey: null,
  },
  showUndoMerge: false,
};

tattoo.undo = {
  lastMerge: null,
};

ensureAppearanceDefaults(tattoo.sex);

export const APPEARANCE_OPTIONS = {
  male: {
    top: ["none", "tshirt", "hoodie", "tank"],
    bottom: ["none", "jeans", "shorts"],
    hair: ["none", "hair_01", "hair_03"],
  },
  female: {
    top: ["none", "bikini_top", "crop_top"],
    bottom: ["none", "bikini_bottom", "skirt"],
    hair: ["none", "hair_01", "hair_02", "hair_03"],
  },
};
// src/tattoo/state.js

export function resetProjectToDefaults({ sex = "female" } = {}) {
  // choose default sex
  tattoo.sex = sex;

  // wipe design
  tattoo.layers = [];
  tattoo.editingLayerId = null;
  tattoo.decalMode = "view";

  // wipe project naming
  tattoo.projectName = "";
  tattoo.fileName = "";

  // wipe body shaping / pose
  tattoo.poseWeights = {
    POSE__A_POSE: 1,
    "POSE__A-Pose": 0,
    POSE__Neutral: 0,
  };

  // Reset body morph sliders. Without this, morph values from a previous
  // project carry over into "fresh" projects because resetProjectToDefaults
  // never touched tattoo.morphs. Mirror the initial-state defaults so any
  // consumer reading e.g. tattoo.morphs.height still sees a number, not
  // undefined.
  tattoo.morphs = {
    height: 0,
    weight: 0,
    muscular: 0,
    chest: 0,
    waist: 0,
    hips: 0,
  };

  // reset appearance
  tattoo.skinColor = "#d19771";
  tattoo.appearance = {
    hair: "hair_01",
    hairColorMode: "preset",
    hairColorPreset: "black", // swap if needed
    hairColorCustom: "#2b1d17",
  };

  // reset studio/view state
  tattoo.viewState = null;
  tattoo.readOnly = false;
  tattoo.panelMode = "flash";

  // wipe guide paint
  tattoo.guidePaintPng = null;

  // clear request-ish state if used
  tattoo.requestDraft = null;
  tattoo.requestOpen = false;

  // camera globals
  try {
    window.__tt_recent_viewSnap = null;
    window.__tt_lock_cam = null;
    window.__tt_cam_lock_until = 0;
    window.__tt_cam_gesture = false;
    window.__tt_pending_share = null;
    window.__tt_shared_cam_applied = false;
    window.__tt_skip_fit_once = false;
  } catch {}
}

// Returns the current project's slot ID, or null if none is set.
//
// ⚠️ IMPORTANT: this is a pure GETTER. It must NEVER auto-generate an
// ID or mutate tattoo.projectId. The previous version did — if
// tattoo.projectId was empty AND projectName was empty AND fileName was
// empty, it'd invent `local:tattune:female` (from the "Tattune" name
// fallback) and assign that as the current slot. Any code that then
// touched autosave / thumbnail-capture / recents would write a phantom
// "Tattune" project into the dashboard — completely disconnected from
// whatever the user was actually editing. Symptom: a mystery "Tattune"
// card spawning after navigating back to dashboard.
//
// All callers already handle null gracefully (early-return when there's
// no slot ID). Slot IDs are assigned intentionally in exactly two
// places: handleNewSession (creates a new slot) and the various load
// paths (loadProjectFromData / handleOpenRecent / file open, which pin
// the loaded slot's ID). Anywhere else asking "what's the current
// slot?" is a read-only question with a legitimate "there isn't one"
// answer.
export function getCurrentProjectId() {
  if (tattoo.projectId && typeof tattoo.projectId === "string") {
    return tattoo.projectId;
  }
  return null;
}

export function beginLayerDrag(layerId) {
  tattoo.draggingLayerId = layerId;
}

export function endLayerDrag(layerId) {
  if (tattoo.draggingLayerId === layerId) {
    tattoo.draggingLayerId = null;
  }
}

export const autoDeleteOnContextChange = false;

/* ---------------- Context pruning ---------------- */
// We now keep *all* layers in tattoo.layers and filter by context when rendering.
// No more deleting other-sex / other-part layers.
export function pruneLayersForContext() {
  // intentionally a no-op; kept for backwards compatibility
}

// Ensure editingLayerId always points to a layer that matches current sex/part.
export function clearEditingIfContextMismatch() {
  const { sex, part } = _currentContext();
  const cur = tattoo.layers.find((l) => l.id === tattoo.editingLayerId);
  if (cur && _matchesContext(cur, sex, part)) return;

  const first = (tattoo.layers || []).find((L) =>
    _matchesContext(L, sex, part)
  );
  tattoo.editingLayerId = first?.id ?? null;
  bump();
}

/* ---------------- Small helpers ---------------- */
export function guideKeyFor(sex, view, part) {
  return view === "fullbody" ? `fullbody_${sex}` : part || "torso";
}
export function getActivePartKey() {
  const { part } = _currentContext();
  return part;
}

// Switch sex, keep all layers, and pick an editing layer that matches.
export function setSex(next) {
  tattoo.sex = next === "male" ? "male" : "female";
  tattoo.viewMode = "fullbody";

  ensureAppearanceDefaults(tattoo.sex); // ✅ add this line

  clearEditingIfContextMismatch();
  bump();
}

export function setViewMode(next) {
  // Toggle how we *view* the body, without touching stored layers.
  tattoo.viewMode = next === "fullbody" ? "fullbody" : "parts";

  clearEditingIfContextMismatch();
  bump();
}

export function togglePart(key, on) {
  // Turn on/off a part flag (and solo it in "parts" mode),
  // but again: no layer reshuffling.
  tattoo.parts[key] = !!on;

  if (tattoo.viewMode === "parts") {
    Object.keys(tattoo.parts).forEach((k) => {
      if (k !== key) tattoo.parts[k] = false;
    });
  }

  clearEditingIfContextMismatch();
  bump();
}

/* ---------------- Layer helpers ---------------- */
export function setLayerVisible(id, on) {
  const L = tattoo.layers.find((l) => l.id === id);
  if (!L) return;
  L.visible = !!on;
  bump();
}
export function setLayerName(id, name) {
  const L = tattoo.layers.find((l) => l.id === id);
  if (!L || L.locked) return;
  L.name = String(name ?? "").slice(0, 64);
  bump();
}
export function setLayerBlend(id, mode) {
  const L = tattoo.layers.find((l) => l.id === id);
  if (!L || L.locked) return;

  const m = String(mode || "").toLowerCase();
  // only ever store "skin" or "ink"
  L.blend = m === "ink" ? "ink" : "skin";
  bump();
}
export function setLayerOpacity(id, value) {
  const L = tattoo.layers.find((l) => l.id === id);
  if (!L) return;
  let v = typeof value === "string" ? parseFloat(value) : value;
  if (v > 1) v = v / 100;
  L.opacity = Math.min(1, Math.max(0, v));
  bump();
}
export function setLayerExpanded(id, on) {
  const L = tattoo.layers.find((l) => l.id === id);
  if (!L) return;
  L._expanded = !!on;
  tattoo.editingLayerId = on
    ? id
    : tattoo.editingLayerId === id
    ? null
    : tattoo.editingLayerId;
  bump();
}
export function moveLayer(id, dir /* "up"|"down" */) {
  const arr = tattoo.layers;
  const i = arr.findIndex((l) => l.id === id);
  if (i < 0 || arr[i]?.locked) return;
  const j = dir === "up" ? i - 1 : i + 1;
  if (j < 0 || j >= arr.length || arr[j]?.locked) return;
  const tmp = arr[i];
  arr[i] = arr[j];
  arr[j] = tmp;
  bump();
}
export function removeLayer(id) {
  const arr = tattoo.layers;
  const i = arr.findIndex((l) => l.id === id);
  if (i < 0 || arr[i]?.locked) return;
  arr.splice(i, 1);
  if (tattoo.editingLayerId === id) tattoo.editingLayerId = null;
  bump();
}

export async function mergeLayerDown(id) {
  const arr = tattoo.layers || [];
  const i = arr.findIndex((l) => l.id === id);
  if (i < 0) return;
  if (arr[i]?.locked) return;

  const j = i + 1; // “down” = below in list
  if (j >= arr.length) return;
  if (arr[j]?.locked) return;

  const top = arr[i];
  const bottom = arr[j];

  const mergeSize = tattoo.renderQuality === "drag" ? 1024 : 2048;

  const dataURL = await mergeLayersToDataURL(bottom, top, {
    size: mergeSize,
    edgeClip: tattoo.edgeClip ?? 0.15,
  });
  if (!dataURL) return;

  // ✅ mimic addLayerFromFile shape
  const sex = bottom.sex ?? top.sex ?? _currentContext().sex;
  const part = bottom.part ?? top.part ?? _currentContext().part;

  const merged = layerDefaults({
    name: `${top.name || "Layer"} + ${bottom.name || "Layer"}`,
    url: dataURL,
    transform: { tx: 0.5, ty: 0.5, scale: 0.5, rot: 0 },
    sex,
    part,
    partKeys: [part],
    // Optional: if your app expects merged to behave like a normal image layer:
    // blend: "ink",
    // isDecal: false,
  });

  // Replace [top, bottom] with merged in-place
  arr.splice(i, 2, merged);

  tattoo.editingLayerId = merged.id;

  // ✅ mimic importer’s “finalize transform”
  setLayerTransform(merged.id, { tx: 0.5, ty: 0.5, scale: 1, rot: 0 });

  bump();

  try {
    window.dispatchEvent(
      new CustomEvent("tt:open-layer", { detail: { layerId: merged.id } })
    );
  } catch {}
}

export function setLayerProp(id, key, value) {
  const i = tattoo.layers.findIndex((l) => l.id === id);
  if (i < 0 || tattoo.layers[i]?.locked) return;

  const cur = tattoo.layers[i];

  // ✅ FAST PATH: during drag, mutate in-place (no array rebuild)
  if (window.__tt_decal_drag_active) {
    if (key === "transform") {
      cur.transform = normalizeTransform({
        ...(cur.transform || {}),
        ...(value || {}),
      });
    } else {
      cur[key] = value;
    }
    return;
  }

  // normal path
  const next =
    key === "transform"
      ? {
          ...cur,
          transform: normalizeTransform({
            ...(cur.transform || {}),
            ...(value || {}),
          }),
        }
      : { ...cur, [key]: value };

  tattoo.layers = [
    ...tattoo.layers.slice(0, i),
    next,
    ...tattoo.layers.slice(i + 1),
  ];
  bump();
}

export function setLayerTransform(id, patch) {
  const i = tattoo.layers.findIndex((l) => l.id === id);
  if (i < 0 || tattoo.layers[i]?.locked) return;

  // ✅ FAST PATH during drag: mutate in place, no array rebuild
  if (window.__tt_decal_drag_active) {
    const cur = tattoo.layers[i];
    cur.transform = normalizeTransform({
      ...(cur.transform || {}),
      ...(patch || {}),
    });
    return;
  }
  // ✅ FAST PATH: during drag, do NOT rebuild tattoo.layers array
  if (window.__tt_decal_drag_active) {
    cur.transform = nextTransform; // valtio tracks this mutation
    return;
  }

  // ✅ NORMAL PATH (non-drag): immutable replace + bump
  const cur = tattoo.layers[i];
  const next = {
    ...cur,
    transform: normalizeTransform({
      ...(cur.transform || {}),
      ...(patch || {}),
    }),
  };

  tattoo.layers = [
    ...tattoo.layers.slice(0, i),
    next,
    ...tattoo.layers.slice(i + 1),
  ];

  bump();
}
export function toggleLayerVisibility(id) {
  const i = tattoo.layers.findIndex((l) => l.id === id);
  if (i < 0) return;
  const cur = tattoo.layers[i];
  const next = { ...cur, visible: !cur.visible };
  tattoo.layers = [
    ...tattoo.layers.slice(0, i),
    next,
    ...tattoo.layers.slice(i + 1),
  ];
  bump();
}
export const toggleLayerVisible = toggleLayerVisibility;

/* ---------------- Skin / Morph setters ---------------- */
export function setSkinColor(hex) {
  try {
    const v = String(hex || "").trim();
    const m = v.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!m) return;
    const h = v.startsWith("#") ? v : `#${v}`;
    tattoo.skinColor = h;
    bump();
  } catch {}
}
export function setMorph(key, value) {
  const v = Number(value);
  if (!Number.isFinite(v)) return;
  tattoo.morphs = { ...(tattoo.morphs || {}), [key]: v };
  bump();
}
export function setMorphs(patch = {}) {
  tattoo.morphs = { ...(tattoo.morphs || {}), ...(patch || {}) };
  bump();
}

const POSE_PREFIX = "POSE__";

export function setPoseMorphNames(names) {
  tattoo.poseMorphNames = Array.isArray(names) ? names : [];
  // ensure weights keys exist
  for (const n of tattoo.poseMorphNames) {
    if (tattoo.poseWeights[n] == null) tattoo.poseWeights[n] = 0;
  }
  // default neutral to 1 if present and nothing else set
  const neutral = tattoo.poseMorphNames.find(
    (n) => n === `${POSE_PREFIX}Neutral`
  );

  if (neutral) {
    const anyActive = tattoo.poseMorphNames.some(
      (n) => (tattoo.poseWeights[n] || 0) > 0.001
    );

    if (!anyActive) {
      for (const n of tattoo.poseMorphNames) {
        tattoo.poseWeights[n] = 0;
      }
      tattoo.poseWeights[neutral] = 1;
    }
  }
}

// this is the “go through neutral” brain
export function setPoseWeight(targetName, targetValue, opts = {}) {
  const ms = opts.ms ?? 220;
  const neutralName = `${POSE_PREFIX}Neutral`;

  // clamp
  const v = Math.max(0, Math.min(1, Number(targetValue) || 0));

  // if pose list not ready, just set directly
  if (!tattoo.poseMorphNames?.length) {
    tattoo.poseWeights[targetName] = v;
    bump(); // ✅
    return;
  }

  const names = tattoo.poseMorphNames.slice();
  const hasNeutral = names.includes(neutralName);

  // EXCLUSIVE poses: one active at a time (besides neutral)
  const isTargetNeutral = targetName === neutralName;

  // capture start snapshot
  const start = {};
  for (const n of names) start[n] = tattoo.poseWeights[n] || 0;

  // Phase 1: fade all non-neutral -> 0, neutral -> 1
  const phase1To = {};
  for (const n of names) phase1To[n] = 0;
  if (hasNeutral) phase1To[neutralName] = 1;

  const t0 = performance.now();
  function easeOutQuad(u) {
    return u * (2 - u);
  }

  function lerp(a, b, u) {
    return a + (b - a) * u;
  }

  function applyWeights(map) {
    for (const k of names) tattoo.poseWeights[k] = map[k] || 0;
    bump(); // ✅ important: make it persist/dirty/autosave
  }

  function tickPhase1(now) {
    const u = Math.min(1, (now - t0) / Math.max(1, ms));
    const e = easeOutQuad(u);
    const cur = {};
    for (const n of names) cur[n] = lerp(start[n], phase1To[n], e);
    applyWeights(cur);
    if (u < 1) requestAnimationFrame(tickPhase1);
    else {
      // Phase 2:
      // - if target is neutral: stay neutral(1) and done
      // - else: fade target -> v and neutral -> 0
      if (isTargetNeutral || !hasNeutral) {
        // neutral only mode
        const cur2 = {};
        for (const n of names) cur2[n] = 0;
        if (hasNeutral) cur2[neutralName] = 1;
        applyWeights(cur2);
        return;
      }

      const start2 = {};
      for (const n of names) start2[n] = tattoo.poseWeights[n] || 0;

      const to2 = {};
      for (const n of names) to2[n] = 0;
      to2[targetName] = v;
      to2[neutralName] = 0;

      const t1 = performance.now();
      function tickPhase2(now2) {
        const u2 = Math.min(1, (now2 - t1) / Math.max(1, ms));
        const e2 = easeOutQuad(u2);
        const curB = {};
        for (const n of names) curB[n] = lerp(start2[n], to2[n], e2);
        applyWeights(curB);
        if (u2 < 1) requestAnimationFrame(tickPhase2);
      }
      requestAnimationFrame(tickPhase2);
    }
  }

  requestAnimationFrame(tickPhase1);
}

// --- Body context apply (from viewer comments attachment) ---
export function applyBodyContext(payload, opts = {}) {
  if (!payload || typeof payload !== "object") return false;

  // support both shapes:
  // { morphs, skinColor }  OR  { morph, skinColor }
  const incomingMorphs =
    payload.morphs ||
    payload.morph ||
    payload.body?.morphs ||
    payload.body?.morph ||
    null;

  const incomingSkin =
    payload.skinColor ||
    payload.skin ||
    payload.body?.skinColor ||
    payload.body?.skin ||
    null;

  // Optional: allow sex/viewMode if you ever include it
  if (opts.allowSex && typeof payload.sex === "string") {
    tattoo.sex = payload.sex === "male" ? "male" : "female";
  }
  if (opts.allowView && typeof payload.viewMode === "string") {
    tattoo.viewMode = payload.viewMode;
  }

  if (incomingSkin) setSkinColor(incomingSkin);

  if (incomingMorphs && typeof incomingMorphs === "object") {
    const clean = {};
    for (const [k, v] of Object.entries(incomingMorphs)) {
      const n = Number(v);
      if (Number.isFinite(n)) clean[k] = n;
    }

    if (opts.replace) tattoo.morphs = { ...clean }; // true replace
    else setMorphs(clean); // merge patch
  }

  bump();
  markSaved("body-context-apply");
  return true;
}

/* ---------------- Suggestion layers ---------------- */
export function addSuggestionLayer(partial, author = "Guest") {
  const { sex, part } = _currentContext();
  const L = layerDefaults({
    ...partial,
    sex: partial?.sex ?? sex,
    part: partial?.part ?? part,
    partKeys: partial?.partKeys ?? [part],
    suggestion: true,
    suggestedBy: author || "Guest",
  });
  tattoo.layers = [L, ...tattoo.layers];
  tattoo.editingLayerId = L.id;
  tattoo.stage = "design";
  bump();
  return L;
}
export function acceptSuggestion(id) {
  const i = tattoo.layers.findIndex((l) => l.id === id);
  if (i < 0) return;
  const cur = tattoo.layers[i];
  const next = { ...cur, suggestion: false, suggestedBy: null };
  tattoo.layers = [
    ...tattoo.layers.slice(0, i),
    next,
    ...tattoo.layers.slice(i + 1),
  ];
  bump();
}

// ---- Persist morph changes coming from UI sliders / meshes ----
try {
  window.addEventListener("morph:set", (e) => {
    const { name, value } = (e && e.detail) || {};
    if (typeof name === "string" && Number.isFinite(value)) {
      setMorph(name, value); // updates tattoo.morphs
      try {
        window.dispatchEvent(new Event("tt:autosave-flush"));
      } catch {}
    }
  });
} catch {}

/* ---------------- Replace image (persistent) ---------------- */
export async function replaceLayerImage(layerId, fileOrUrl, opts = {}) {
  let url = "";
  if (typeof fileOrUrl === "string") {
    url = fileOrUrl;
  } else if (fileOrUrl instanceof File) {
    url = await readFileAsDataURL(fileOrUrl); // persistent
  }
  if (!url) return;

  const i = tattoo.layers.findIndex((l) => l.id === layerId);
  if (i < 0 || tattoo.layers[i]?.locked) return;

  const cur = tattoo.layers[i];
  const next = { ...cur, url, name: opts.name ?? cur.name };

  tattoo.layers = [
    ...tattoo.layers.slice(0, i),
    next,
    ...tattoo.layers.slice(i + 1),
  ];
  bump();
}

// ----- Simple transform undo stack per layer -----
const _transformHistory = Object.create(null); // { [layerId]: [{tx,ty,scale,rot}, ...] }
const UNDO_LIMIT = 20;

export function pushTransformUndo(layerId, prevTransform) {
  if (!layerId || !prevTransform) return;
  const arr = _transformHistory[layerId] || (_transformHistory[layerId] = []);
  arr.push({ ...prevTransform });
  if (arr.length > UNDO_LIMIT) arr.shift();
}

export function canUndoTransform(layerId) {
  const arr = _transformHistory[layerId] || [];
  return arr.length > 0;
}

export function undoTransform(layerId) {
  const i = tattoo.layers.findIndex((l) => l.id === layerId);
  if (i < 0) return false;
  const arr = _transformHistory[layerId] || [];
  const prev = arr.pop();
  if (!prev) return false;

  const cur = tattoo.layers[i];
  tattoo.layers = [
    ...tattoo.layers.slice(0, i),
    { ...cur, transform: normalizeTransform(prev) },
    ...tattoo.layers.slice(i + 1),
  ];
  bump();
  return true;
}

/* ---------------- Serialization helpers ---------------- */
async function serializeLayersPersistent(layers) {
  return await Promise.all(
    (layers || []).map(async (layer) => {
      let url = layer.url || "";
      if (typeof url === "string" && url.startsWith("blob:")) {
        try {
          url = await blobUrlToDataURL(url); // make portable
        } catch {}
      }
      return {
        id: layer.id,
        name: layer.name,
        url,
        visible: layer.visible,
        opacity: layer.opacity,
        blend: layer.blend,
        edgeClip: layer.edgeClip,
        partKeys: layer.partKeys,
        transform: layer.transform,
        sex: layer.sex,
        part: layer.part,
        anchor: layer.anchor ?? null,
        locked: !!layer.locked,
        suggestion: !!layer.suggestion,
        suggestedBy: layer.suggestedBy ?? null,
        createdAt: layer.createdAt ?? Date.now(),
      };
    })
  );
}

export function getProjectJSON() {
  // Ensure we have a stable id for the current project
  const projectId = tattoo.projectId || getCurrentProjectId();

  // 🔴 NEW: snapshot the guide-paint canvas if it has strokes
  let guidePaintPng = null;
  try {
    const gp = guidePaintRef.current;
    if (
      gp &&
      typeof gp.getCanvas === "function" &&
      typeof gp.hasStrokes === "function" &&
      gp.hasStrokes()
    ) {
      const canvas = gp.getCanvas();
      if (canvas && canvas.toDataURL) {
        guidePaintPng = canvas.toDataURL("image/png");
      }
    }
  } catch (e) {
    console.warn("[state] failed to capture guide paint PNG:", e);
  }

  const data = {
    version: 2,

    // identity / metadata so snapshots are project-aware
    projectId,
    projectName: tattoo.projectName || null,
    fileName: tattoo.fileName || null,

    // NEW-ish meta
    sex: tattoo.sex,
    viewMode: tattoo.viewMode,
    viewState: tattoo.viewState || null,
    appearance: { ...tattoo.appearance },

    skinColor: tattoo.skinColor,
    edgeClip: tattoo.edgeClip,
    parts: tattoo.parts,
    morphs: tattoo.morphs || {},
    poseWeights: tattoo.poseWeights || {},
    reactions: exportCountsOnly(),

    layers: (tattoo.layers || []).map((layer) => ({
      id: layer.id,
      name: layer.name,
      url: layer.url,

      // ✅ add these three (and make sure the variable is `layer`, not `l`)
      source: layer.source ?? null,
      ownerShopId: layer.ownerShopId ?? null,
      ownerShopName: layer.ownerShopName ?? null,
      ownerShopSlug: layer.ownerShopSlug ?? null,

      visible: layer.visible,
      opacity: layer.opacity,
      blend: layer.blend,
      edgeClip: layer.edgeClip,
      partKeys: layer.partKeys,
      transform: layer.transform,
      sex: layer.sex,
      part: layer.part,
      locked: !!layer.locked,
      suggestion: !!layer.suggestion,
      suggestedBy: layer.suggestedBy ?? null,
      createdAt: layer.createdAt ?? Date.now(),

      // Type tool: text layers keep their editable settings so they can be
      // re-edited after save/reload (url is the rendered image).
      kind: layer.kind ?? null,
      text: layer.kind === "text" ? layer.text ?? null : null,
    })),

    comments: commentsStore.threads || {},

    // 🔴 NEW: baked guide-paint data URL (may be null)
    guidePaintPng,
  };

  return JSON.stringify(data);
}
export async function saveProjectToFile() {
  const layersOut = await serializeLayersPersistent(tattoo.layers);

  const projectName =
    (tattoo.projectName && tattoo.projectName.trim()) || "Tattune";

  const data = {
    version: 2,
    projectName, // store the friendly name in the file
    sex: tattoo.sex,
    viewMode: tattoo.viewMode,
    viewState: tattoo.viewState || null,
    skinColor: tattoo.skinColor,
    edgeClip: tattoo.edgeClip,
    parts: tattoo.parts,
    morphs: tattoo.morphs || {},
    poseWeights: tattoo.poseWeights || {},
    layers: layersOut,
    comments: commentsStore.threads || {},
    guidePaintPng: tattoo.guidePaintPng || null,
  };

  // 🔹 Nice filename + custom extension (still plain JSON inside)
  const sexLabel = tattoo.sex === "male" ? "male" : "female";
  const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, ""); // 20251206
  const filename = `${projectName}-${sexLabel}-${stamp}.tattune`;

  // 🔹 Update "Recent projects" with this specific file
  try {
    const thumb = localStorage.getItem("tattoo:last-preview:v1") || null;

    // unique-ish id per saved file; lets you have multiple recents
    const projectId = getCurrentProjectId();

    upsertRecentProject({
      id: projectId,
      projectName,
      sex: tattoo.sex,
      thumb: thumb || undefined,
    });
  } catch (e) {
    console.warn("[recent] upsert from saveProjectToFile failed:", e);
  }

  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });

  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();

  try {
    const { markJustSaved } = await import("./autosave.js");
    markJustSaved();
  } catch {}

  window.dispatchEvent(new Event("tattoo:just-saved"));

  a.remove();
}

// 🔹 NEW: pure “from JSON object” loader that autosave can use
/**
 * Load a saved project snapshot into the live `tattoo` proxy.
 *
 * explicitProjectId — when the caller already knows which slot ID this
 * snapshot was loaded FROM (e.g. Dashboard.handleOpenRecent clicked a
 * specific recent card with id `local:new:...`), it passes that ID
 * here. We then SET tattoo.projectId from this value unconditionally,
 * which guarantees subsequent autosaves write back to the same slot
 * even if the snapshot's internal data.projectId is missing or stale.
 *
 * Bug this prevents: previously we only updated tattoo.projectId from
 * data.projectId when present, and otherwise left whatever the editor
 * had cached from the PREVIOUS session. That caused autosaves to
 * write project B's edits into project A's slot — corrupting A and
 * spawning duplicate "B" rows in recents on every autosave tick.
 *
 * Precedence: explicitProjectId > data.projectId > existing tattoo.projectId.
 */
export function loadProjectFromData(data, explicitProjectId = null) {
  if (!data || !Array.isArray(data.layers)) {
    throw new Error("Bad project");
  }

  // meta
  //
  // ⚠️ These fields must be REPLACED, not merged. Previously we only
  // overwrote projectName / fileName when the incoming data had a value,
  // which meant the PREVIOUS project's name persisted in memory when
  // loading a partial snapshot. Autosave then wrote that stale name
  // back to the new slot, permanently corrupting it on disk. Symptom:
  // Debbie's slot ends up named "Laura" after opening Laura → Debbie.
  //
  // "Untitled" (not "Tattune") is the default here — "Tattune" is the
  // app's brand name and using it as a fallback made corruption visually
  // indistinguishable from the app's own default state. Matches
  // ProjectNameField's on-blur fallback so the same string surfaces in
  // both places.
  tattoo.projectName =
    typeof data.projectName === "string" && data.projectName.trim()
      ? data.projectName.trim()
      : "Untitled";

  tattoo.fileName =
    typeof data.fileName === "string" && data.fileName.trim()
      ? data.fileName.trim()
      : null;

  if (typeof explicitProjectId === "string" && explicitProjectId.trim()) {
    tattoo.projectId = explicitProjectId.trim();
  } else if (typeof data.projectId === "string" && data.projectId.trim()) {
    tattoo.projectId = data.projectId.trim();
  }
  // else leave tattoo.projectId at whatever was set before — only
  // happens for legacy / orphan snapshots called from a path that
  // doesn't know its slot ID. That's a no-fix-on-restore degenerate
  // case, not a fresh corruption vector.

  // 🔴 NEW: stash guide-paint PNG (if present)
  tattoo.guidePaintPng = data.guidePaintPng || null;

  // basic props
  tattoo.skinColor = data.skinColor ?? tattoo.skinColor;
  // view restore (support old + new keys)
  const vs = data?.viewState ?? data?.viewSnap ?? null;
  tattoo.viewState = vs;
  tattoo.viewSnap = vs;
  tattoo.edgeClip = data.edgeClip ?? tattoo.edgeClip;

  // ⚠️ REPLACE, do not merge. These four fields are all fully
  // serialized by autosave — the snapshot represents the complete
  // desired state, not a patch on top of whatever was previously in
  // memory. Merging leaked the previous project's morphs / parts /
  // appearance / poseWeights into the newly-loaded slot, and the next
  // autosave tick wrote that Frankenstein state back to disk. Symptom:
  // opening Debbie showed Laura's skin/hair/morphs and eventually
  // overwrote Debbie's saved snapshot with Laura's data.
  tattoo.parts = data.parts ? { ...data.parts } : {};
  tattoo.morphs = data.morphs ? { ...data.morphs } : {};
  tattoo.poseWeights = data.poseWeights ? { ...data.poseWeights } : {};

  tattoo.sex = data.sex ?? tattoo.sex ?? "female";

  const defaults = {
    top: "none",
    bottom: "none",
    hair: "none",
    hairColorMode: "preset",
    hairColorPreset: "brown",
    hairColorCustom: "#3b2a1a",
  };

  // Same rule: incoming data wins entirely; defaults fill only the
  // slots the snapshot didn't set. Previously the previous project's
  // appearance sat in the middle of this spread and leaked through
  // whenever the incoming data lacked a given key.
  tattoo.appearance = {
    ...defaults,
    ...(data.appearance || {}),
  };

  // fill any missing keys using defaults for this sex
  ensureAppearanceDefaults(tattoo.sex);
  if (data.viewMode) tattoo.viewMode = data.viewMode;
  // ✅ restore garments/hair from saved project/autosave

  // hydrate layers
  tattoo.layers = data.layers.map((src) =>
    layerDefaults({
      ...src,

      source: src.source ?? null,
      ownerShopId: src.ownerShopId ?? null,
      ownerShopName: src.ownerShopName ?? null,
      ownerShopSlug: src.ownerShopSlug ?? null,
      transform: normalizeTransform(src.transform),
      sex: src.sex,
      part: src.part,
      anchor: src.anchor ?? null,
      locked: !!src.locked,
      suggestion: !!src.suggestion,
      suggestedBy: src.suggestedBy ?? null,
      createdAt: src.createdAt ?? Date.now(),
    })
  );

  // pick an editing layer that matches current context if possible
  {
    const { sex, part } = _currentContext();
    const first = (tattoo.layers || []).find((L) =>
      _matchesContext(L, sex, part)
    );
    tattoo.editingLayerId = first?.id ?? tattoo.layers[0]?.id ?? null;
  }

  // comments + reactions
  commentsStore.threads = extractCommentsFromData(data);
  applyProjectReactions(data.reactions || {});

  bump();

  console.log(
    "[restore] layers after load",
    tattoo.layers.map((l) => ({
      name: l.name,
      ownerShopId: l.ownerShopId,
      ownerShopName: l.ownerShopName,
      ownerShopSlug: l.ownerShopSlug,
    }))
  );

  markSaved("file-load");

  return true;
}

// 🔹 Wrapper: read a file, then delegate to loadProjectFromData
// state.js (or wherever this lives)
// state.js
export async function loadProjectFromFile(file) {
  const reader = new FileReader();

  reader.onload = async () => {
    window.__lastLoadedFilename = file.name.replace(/\.(tattune|json)$/i, "");

    try {
      const raw = reader.result;
      const data = JSON.parse(raw);

      // (A) Load first (if loadProjectFromData can be async, await it)
      const ok = await loadProjectFromData(data);

      if (ok !== false) {
        // (B) Then set name + fileName + projectId (same as you already do)
        try {
          const metaName =
            data?.meta?.projectName || data?.projectName || data?.name || null;

          const fromFile =
            file?.name?.replace(/\.(tattune|json)$/i, "").trim() || null;

          const name =
            (fromFile && String(fromFile).trim()) ||
            (metaName && String(metaName).trim()) ||
            null;

          if (name) {
            const sexLabel = tattoo.sex === "male" ? "male" : "female";

            tattoo.projectName = name;
            tattoo.fileName = fromFile || name;

            const slug = name
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-+|-+$/g, "");

            tattoo.projectId = `file:${slug || "project"}:${sexLabel}`;
          }
        } catch (nameErr) {
          console.warn("[state] failed to set projectName from file:", nameErr);
        }

        // ✅ (C) NOW announce “project is ready”
        try {
          window.dispatchEvent(
            new CustomEvent("tattoo:project-loaded", {
              detail: { projectId: tattoo.projectId, source: "file" },
            })
          );
        } catch {}
      }
    } catch (e) {
      console.warn("Failed to load project:", e);
      toast.error("Could not load project file.");
    }
  };

  reader.readAsText(file);
}

// --- JPEG knockout helpers (self-contained) -----------------
function _smoothstep(edge0, edge1, x) {
  const t = Math.min(
    1,
    Math.max(0, (x - edge0) / Math.max(1e-6, edge1 - edge0))
  );
  return t * t * (3 - 2 * t);
}

/**
 * Convert near-white to transparent + light feather and defringe.
 * Returns a PNG dataURL with alpha.
 */
async function knockoutWhiteToTransparent(dataUrl, opts = {}) {
  const {
    threshold = 248,
    range = 10,
    grow = -0.75,
    blur = 1.5,
    neutral = 255,
  } = opts;

  const img = await loadImage(dataUrl);

  // Canvas setup
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);

  // 1) Build soft alpha from luminance
  let id = ctx.getImageData(0, 0, w, h);
  const d = id.data;
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i],
      g = d[i + 1],
      b = d[i + 2];
    const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const a = 1.0 - _smoothstep(threshold, threshold + range, y);
    d[i + 3] = Math.round(a * 255);
    const k = 1.0 - a;
    d[i] = Math.round(d[i] * a + neutral * k);
    d[i + 1] = Math.round(d[i + 1] * a + neutral * k);
    d[i + 2] = Math.round(d[i + 2] * a + neutral * k);
  }
  ctx.putImageData(id, 0, 0);

  // 2) Optional grow/erode + blur feather
  if (grow !== 0 || blur > 0) {
    const mask = ctx.getImageData(0, 0, w, h);
    const md = mask.data;
    for (let i = 0; i < md.length; i += 4) {
      md[i] = md[i + 1] = md[i + 2] = md[i + 3];
      md[i + 3] = 255;
    }

    const mcan = document.createElement("canvas");
    mcan.width = w;
    mcan.height = h;
    const mctx = mcan.getContext("2d");

    const absGrow = Math.abs(grow);
    mctx.filter = `blur(${absGrow}px)`;
    mctx.drawImage(canvas, 0, 0);
    mctx.filter = "none";

    const mid = mctx.getImageData(0, 0, w, h);
    const md2 = mid.data;
    const cut = 128 + (grow > 0 ? -24 : +24);
    for (let i = 0; i < md2.length; i += 4) {
      const v = md2[i];
      const a2 = v >= cut ? 255 : 0;
      md2[i] = md2[i + 1] = md2[i + 2] = a2;
      md2[i + 3] = 255;
    }
    mctx.putImageData(mid, 0, 0);

    if (blur > 0) {
      const tmp = document.createElement("canvas");
      tmp.width = w;
      tmp.height = h;
      const tctx = tmp.getContext("2d");

      tctx.filter = `blur(${blur}px)`;
      tctx.drawImage(mcan, 0, 0);
      tctx.filter = "none";

      const finalRGBA = ctx.getImageData(0, 0, w, h);
      const fr = finalRGBA.data;
      const blurred = tctx.getImageData(0, 0, w, h).data;
      for (let i = 0; i < fr.length; i += 4) {
        fr[i + 3] = blurred[i];
      }
      ctx.putImageData(finalRGBA, 0, 0);
    } else {
      const finalRGBA = ctx.getImageData(0, 0, w, h);
      const fr = finalRGBA.data;
      const maskData = mctx.getImageData(0, 0, w, h).data;
      for (let i = 0; i < fr.length; i += 4) {
        fr[i + 3] = maskData[i];
      }
      ctx.putImageData(finalRGBA, 0, 0);
    }
  }

  return canvas.toDataURL("image/png");
}

/* ---------------- UV exports ---------------- */
export async function exportUVSheetPNG(opts = {}) {
  const { includeGuide = false, guideAlpha: alphaOpt } = opts;

  const { sex, part } = _currentContext();
  const view = tattoo.viewMode || "fullbody";

  const activeLayers = (tattoo.layers || []).filter(
    (L) => L.visible !== false && _matchesContext(L, sex, part)
  );
  if (!activeLayers.length) {
    toast("No visible layers for the current body part.");
    return;
  }

  // Fixed output resolution on every device. Previously this copied the
  // first layer's source-image size, so a small decal (or an iPad upload
  // that got downscaled) produced a tiny sheet — e.g. 499×499 on iPad.
  // The UV sheet is a stencil/placement reference and should always be
  // full-res. 2048² (~4.2 MP) is well inside iOS Safari's canvas limit
  // (~16.7 MP), so this is safe on iPad and iPhone too.
  const size = 2048;

  const guideUrl = includeGuide ? uvGuideUrlFor(sex, view) : null;
  const guideAlpha = includeGuide
    ? typeof alphaOpt === "number"
      ? alphaOpt
      : 0.6
    : 0;

  const canvas = await buildFlattenedUV(activeLayers, {
    size,
    edgeClip: tattoo.edgeClip,
    guideUrl,
    guideAlpha,
  });

  canvas.toBlob((blob) => {
    if (!blob) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `Tattune_Layout-${part}${includeGuide ? "-GUIDE" : ""}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, "image/png");
}

/**
 * Export each design on the current body part as its own upright,
 * tightly-cropped 2048px PNG (overlapping layers count as one design).
 * One design → single PNG download; several → one zip.
 */
export async function exportDesignsPNG() {
  const { sex, part } = _currentContext();
  const activeLayers = (tattoo.layers || []).filter(
    (L) => L.visible !== false && _matchesContext(L, sex, part)
  );
  if (!activeLayers.length) {
    toast("No visible layers for the current body part.");
    return;
  }

  const designs = await buildDesignExports(activeLayers, {
    size: 2048,
    edgeClip: tattoo.edgeClip,
    target: 2048,
  });
  if (!designs.length) {
    toast("Nothing to export.");
    return;
  }

  const safe = (n) =>
    String(n || "design").replace(/[\\/:*?"<>|]+/g, "").trim().slice(0, 40) || "design";
  const toBlob = (c) => new Promise((res) => c.toBlob(res, "image/png"));
  const download = (blob, filename) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  };

  if (designs.length === 1) {
    const blob = await toBlob(designs[0].canvas);
    if (blob) download(blob, `Tattune_${safe(designs[0].name)}.png`);
    return;
  }

  const used = new Map();
  const files = [];
  for (const d of designs) {
    const blob = await toBlob(d.canvas);
    if (!blob) continue;
    let base = safe(d.name);
    const n = (used.get(base) || 0) + 1;
    used.set(base, n);
    if (n > 1) base = `${base}-${n}`;
    files.push({
      name: `${base}.png`,
      data: new Uint8Array(await blob.arrayBuffer()),
    });
  }
  download(makeZip(files), `Tattune_Designs-${part}.zip`);
}

async function prepUpload(file) {
  // Cap every upload at 2048 on its long edge, on every device. That
  // matches the UV export resolution exactly (so no quality is lost), and
  // stops 12 MP phone photos from being held in memory at full size —
  // which is what actually makes iPad Safari fragile. Previously iOS was
  // capped lower (1536), which made iPad exports soft.
  //
  // If decoding fails (e.g. a format createImageBitmap can't read), fall
  // back to the original file rather than blocking the upload.
  if (!file) return file;
  try {
    return await downscaleImageFile(file, 2048);
  } catch (e) {
    console.warn("[prepUpload] downscale failed, using original file:", e);
    return file;
  }
}

/* ---------------- Layers API (add) ---------------- */
export async function addLayerFromFile(file) {
  if (!file) return;
  const safeFile = await prepUpload(file);
  const url = await readFileAsDataURL(safeFile || file); // persistent
  const { sex, part } = _currentContext();

  const layer = layerDefaults({
    name: file.name.replace(/\.[^.]+$/, ""),
    url,
    transform: { tx: 0.5, ty: 0.5, scale: 0.5, rot: 0 },
    sex,
    part,
    partKeys: [part],
  });

  tattoo.layers = [layer, ...tattoo.layers];
  tattoo.editingLayerId = layer.id;
  setLayerTransform(layer.id, { tx: 0.5, ty: 0.5, scale: 1, rot: 0 });
  bump();
}

export async function addDecalFromFile(file, opts = {}) {
  const safeFile = await prepUpload(file);
  const { sex, part } = _currentContext();
  const name =
    opts.name || (file?.name ? file.name.replace(/\.[^.]+$/, "") : "decal");

  const qp = new URLSearchParams(window.location.search);
  const urlFlag = qp.get("ko");
  const flagFromUrl = urlFlag === "1" ? true : urlFlag === "0" ? false : null;

  const envWantsKO =
    String(import.meta.env.VITE_KNOCKOUT_JPEG || "").toLowerCase() === "true";
  const explicitOpt =
    typeof opts.knockoutWhite === "boolean" ? opts.knockoutWhite : null;

  const shouldKnockout = flagFromUrl ?? explicitOpt ?? envWantsKO;

  let url = opts.url || "";
  if (!url && file) {
    const raw = await readFileAsDataURL(safeFile || file);
    // JPEG detection checks the ORIGINAL file — the prepared copy is
    // re-encoded as .webp, which would hide the fact it started as a JPEG.
    const isJpeg =
      (file && /jpe?g$/i.test(file.name)) ||
      (file && file.type === "image/jpeg") ||
      /\.jpe?g$/i.test(name);

    if (isJpeg && shouldKnockout) {
      try {
        const params = {
          threshold: Number(import.meta.env.VITE_KO_THRESHOLD ?? 247),
          softness: Number(import.meta.env.VITE_KO_SOFTNESS ?? 18),
          defringe: Number(import.meta.env.VITE_KO_DEFRINGE ?? 0.9),
          erodePx: Number(import.meta.env.VITE_KO_ERODE ?? 0),
        };
        url = await removeWhiteBackgroundPro(raw, params);
      } catch (e) {
        console.warn("[knockout] failed, using raw:", e);
        url = raw;
      }
    } else {
      url = raw;
    }
  }
  if (!url) return;

  const {
    initialScale,
    transform: transformOverride,
    ...restOpts
  } = opts || {};

  const scale = typeof initialScale === "number" ? initialScale : 0.5;

  const L = layerDefaults({
    name,
    url,
    isDecal: true,
    sex,
    part,
    partKeys: [part],
    source: opts.source || "upload", // ✅ ADD THIS LINE
    transform: {
      tx: 0.5,
      ty: 0.5,
      scale,
      rot: 0,
      ...(transformOverride || {}),
    },
    ...restOpts,
  });

  L.source = opts.source || "upload"; // ✅ force it on the final layer object

  tattoo.layers = [L, ...tattoo.layers];
  tattoo.editingLayerId = L.id;
  tattoo.stage = "design";
  bump();
}

export async function addDecalFromUrl(url, opts = {}) {
  if (!url) return;

  const { sex, part } = _currentContext();
  const name = opts.name || "flash";

  const L = layerDefaults({
    name,
    url, // <-- IMPORTANT: direct URL (not a dataURL)
    isDecal: true,
    sex,
    part,
    partKeys: [part],
    transform: {
      tx: 0.5,
      ty: 0.5,
      scale: typeof opts.initialScale === "number" ? opts.initialScale : 0.5,
      rot: 0,
      ...(opts.transform || {}),
    },
    ...opts,
  });

  tattoo.layers = [L, ...tattoo.layers];
  tattoo.editingLayerId = L.id;
  tattoo.stage = "design";
  bump();
}

/* ---------------- Type tool: text layers ---------------- */

// Layers default to the "skin" blend (multiplied like ink), which makes
// white or very light text disappear. Light text gets the "white" blend
// (white-ink look) instead. Only switches between these two automatic
// values, so a blend the user picked by hand is left alone.
function autoBlendForText(color, currentBlend) {
  if (currentBlend && currentBlend !== "skin" && currentBlend !== "white") {
    return currentBlend;
  }
  const hex = String(color || "#000").replace("#", "");
  const full = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex;
  const n = parseInt(full.slice(0, 6), 16);
  if (Number.isNaN(n)) return currentBlend || "skin";
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum > 0.75 ? "white" : "skin";
}

/**
 * Add an editable text layer. Rendered to a transparent PNG at print
 * resolution, then placed like any other decal. The settings live on
 * `layer.text` so the layer can be re-edited later.
 */
export async function addTextLayer(textSettings = {}) {
  const text = { ...TEXT_DEFAULTS, ...textSettings };
  const { url } = await renderTextLayer(text);
  const { sex, part } = _currentContext();

  const L = layerDefaults({
    name: (text.text || "Text").split(/\r?\n/)[0].slice(0, 32) || "Text",
    url,
    kind: "text",
    text,
    blend: autoBlendForText(text.color, null),
    isDecal: true,
    sex,
    part,
    partKeys: [part],
    transform: { tx: 0.5, ty: 0.5, scale: 0.45, rot: 0 },
  });

  tattoo.layers = [L, ...tattoo.layers];
  tattoo.editingLayerId = L.id;
  tattoo.stage = "design";
  bump();
  markDirty("text-add");
  return L.id;
}

/**
 * Re-render an existing text layer with new settings. Placement
 * (transform) is preserved; only the image and name change.
 */
export async function updateTextLayer(id, patch = {}) {
  const L = (tattoo.layers || []).find((l) => l.id === id);
  if (!L || L.kind !== "text") return;
  const text = { ...TEXT_DEFAULTS, ...(L.text || {}), ...patch };
  const { url } = await renderTextLayer(text);

  tattoo.layers = tattoo.layers.map((l) =>
    l.id === id
      ? {
          ...l,
          url,
          text,
          blend: autoBlendForText(text.color, l.blend),
          name: (text.text || "Text").split(/\r?\n/)[0].slice(0, 32) || "Text",
        }
      : l
  );
  bump();
  markDirty("text-edit");
}

export async function addLayerFromCanvas(canvas, opts = {}) {
  if (!canvas) {
    console.warn("[addLayerFromCanvas] No canvas provided.");
    return null;
  }
  const { sex, part } = _currentContext();

  try {
    const dataUrl = canvas.toDataURL("image/png");
    if (!dataUrl || !dataUrl.startsWith("data:image/png")) {
      console.warn("[addLayerFromCanvas] toDataURL failed/empty.");
      return null;
    }

    const L = layerDefaults({
      name: opts.name || "Guide Paint",
      url: dataUrl,
      visible: true,
      opacity: typeof opts.opacity === "number" ? opts.opacity : 1.0,
      blend: opts.blend || "skin",
      edgeClip:
        typeof opts.edgeClip === "number"
          ? opts.edgeClip
          : tattoo.edgeClip ?? 0.15,

      sex: opts.sex ?? sex,
      part: opts.part ?? part,
      partKeys: Array.isArray(opts.partKeys)
        ? opts.partKeys.slice()
        : [opts.partKeys || part],

      transform: opts.transform || { tx: 0.5, ty: 0.5, scale: 1.0, rot: 0 },
      ...opts,
    });

    tattoo.layers = [L, ...(tattoo.layers || [])];
    tattoo.editingLayerId = L.id;
    bump();

    setTimeout(() => bump(), 0);
    return L;
  } catch (err) {
    console.warn("[addLayerFromCanvas] Failed:", err);
    return null;
  }
}

/* ---------------- Selection helpers ---------------- */
export function editLayer(id) {
  tattoo.editingLayerId = id;
  tattoo.stage = "design";
  bump();
}
export function stopEditing() {
  tattoo.editingLayerId = null;
  bump();
}

/* ---------------- Drag flags ---------------- */
export function setDecalDragging(id, on) {
  const L = tattoo.layers.find((l) => l.id === id);
  if (!L) return;
  L._dragging = !!on;
  tattoo.isDecalDragging = !!on;
  bump();
}

/* ---------------- Misc app helpers ---------------- */
function revokeIfBlob(url) {
  try {
    if (url && typeof url === "string" && url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  } catch {}
}
export function setUvImageFromFile(file) {
  revokeIfBlob(tattoo.uvImage);
  tattoo.uvImage = URL.createObjectURL(file);
  bump();
}
export function clearAll() {
  revokeIfBlob(tattoo.uvImage);
  tattoo.uvImage = null;

  tattoo.parts = {
    torso: true,
    armL: false,
    armR: false,
    legL: false,
    legR: false,
    head: false,
  };

  tattoo.layers.forEach((l) => {
    try {
      revokeIfBlob(l.url);
    } catch {}
  });
  tattoo.layers = [];

  tattoo.edgeClip = 0.8;
  tattoo.stage = "design";
  tattoo.editingLayerId = null;

  commentsStore.threads = {};

  bump();
}
export function expandLayer(id) {
  tattoo.editingLayerId = tattoo.editingLayerId === id ? null : id;
  bump();
}
export function isLayerExpanded(id) {
  return tattoo.editingLayerId === id;
}

/* ---------------- Downloads (multi) ---------------- */
export function downloadUVGuide(partKey) {
  const url = tattoo.uvGuides?.[partKey];
  if (!url) {
    toast.error(`No UV guide found for "${partKey}".`);
    return;
  }
  const a = document.createElement("a");
  a.href = url;
  a.download = `${partKey}_uv_template.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
export function downloadAllUVGuides() {
  const guides = tattoo.uvGuides || {};
  const keys = Object.keys(guides).filter((k) => !!guides[k]);
  if (!keys.length) {
    toast.error("No UV guides configured.");
    return;
  }
  keys.forEach((k, i) => {
    setTimeout(() => downloadUVGuide(k), i * 150);
  });
}

function uvGuideUrlFor(sex, view) {
  const s = String(sex || "female")
    .toLowerCase()
    .startsWith("m")
    ? "male"
    : "female";
  const v = String(view || "fullbody")
    .toLowerCase()
    .startsWith("full")
    ? "full"
    : "full";
  return `/tattoo/uv/${s}_${v}_uv.png`;
}

/* ---------------- Share helpers ---------------- */
export async function exportProjectHashAsync() {
  const layersOut = await serializeLayersPersistent(tattoo.layers);
  const data = {
    version: 2,

    sex: tattoo.sex,
    viewMode: tattoo.viewMode,

    skinColor: tattoo.skinColor,
    appearance: { ...tattoo.appearance },
    edgeClip: tattoo.edgeClip,
    parts: tattoo.parts,
    morphs: tattoo.morphs || {},
    poseWeights: tattoo.poseWeights || {},
    layers: layersOut,
    comments: commentsStore.threads || {},
    reactions: exportCountsOnly(),
  };
  return compressToEncodedURIComponent(JSON.stringify(data));
}

export function exportProjectHash() {
  console.log("[exportProjectHash] layer0", data.layers?.[0]);
  const data = {
    version: 2,

    sex: tattoo.sex,
    viewMode: tattoo.viewMode,

    skinColor: tattoo.skinColor,
    appearance: { ...tattoo.appearance },
    edgeClip: tattoo.edgeClip,
    parts: tattoo.parts,
    morphs: tattoo.morphs || {},
    poseWeights: tattoo.poseWeights || {},
    layers: (tattoo.layers || []).map((l) => ({
      id: l.id,
      name: l.name,
      url: l.url,
      source: l.source || null,
      visible: l.visible,
      opacity: l.opacity,
      blend: l.blend,
      edgeClip: l.edgeClip,
      partKeys: l.partKeys,
      transform: l.transform,
      sex: l.sex,
      part: l.part,
      locked: !!l.locked,
      suggestion: !!l.suggestion,
      suggestedBy: l.suggestedBy ?? null,
      ownerShopId: l.ownerShopId ?? null,
      ownerShopName: l.ownerShopName ?? null,
      ownerShopSlug: l.ownerShopSlug || null,
      createdAt: l.createdAt ?? Date.now(),
    })),
    comments: commentsStore.threads || {},
    reactions: exportCountsOnly(),
  };
  return compressToEncodedURIComponent(JSON.stringify(data));
}

export function scrubDeadBlobLayers() {
  const arr = (tattoo.layers || []).slice();
  for (let i = arr.length - 1; i >= 0; i--) {
    const url = arr[i]?.url;
    if (!url || typeof url !== "string") continue;
    if (!url.startsWith("blob:")) continue;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {};
    img.onerror = () => {
      const idx = tattoo.layers.findIndex((l) => l && l.id === arr[i].id);
      if (idx >= 0) {
        tattoo.layers.splice(idx, 1);
        if (tattoo.editingLayerId === arr[i].id) tattoo.editingLayerId = null;
        bump();
      }
    };
    try {
      img.src = url;
    } catch {}
  }
}

export function importProjectHash(encoded) {
  try {
    const json = decompressFromEncodedURIComponent(encoded || "");
    if (!json) return false;
    const data = JSON.parse(json);

    tattoo.sex = data.sex ?? tattoo.sex ?? "female";
    tattoo.viewMode = data.viewMode ?? tattoo.viewMode ?? "fullbody";

    tattoo.skinColor = data.skinColor ?? tattoo.skinColor;
    tattoo.edgeClip = data.edgeClip ?? tattoo.edgeClip;
    tattoo.parts = { ...tattoo.parts, ...(data.parts || {}) };
    tattoo.morphs = { ...(tattoo.morphs || {}), ...(data.morphs || {}) };
    tattoo.poseWeights = {
      ...(tattoo.poseWeights || {}),
      ...(data.poseWeights || {}),
    }; // ✅

    if (data?.appearance) {
      const defaults = {
        top: "none",
        bottom: "none",
        hair: "none",
        hairColorMode: "preset",
        hairColorPreset: "brown",
        hairColorCustom: "#3b2a1a",
      };

      tattoo.appearance = {
        ...defaults,
        ...(tattoo.appearance || {}), // ✅ preserve existing extra keys
        ...(data.appearance || {}), // ✅ incoming wins
      };
    }

    ensureAppearanceDefaults(tattoo.sex);

    tattoo.layers = (data.layers || []).map((src) =>
      layerDefaults({
        ...src,

        source: src.source ?? null,
        ownerShopId: src.ownerShopId ?? null,
        ownerShopName: src.ownerShopName ?? null,
        ownerShopSlug: src.ownerShopSlug ?? null,
        transform: normalizeTransform(src.transform),
        sex: src.sex,
        part: src.part,
        locked: !!src.locked,
        suggestion: !!src.suggestion,
        suggestedBy: src.suggestedBy ?? null,
        createdAt: src.createdAt ?? Date.now(),
      })
    );

    // pick an editing layer that matches current context if possible
    {
      const { sex, part } = _currentContext();
      const first = (tattoo.layers || []).find((L) =>
        _matchesContext(L, sex, part)
      );
      tattoo.editingLayerId = first?.id ?? tattoo.layers[0]?.id ?? null;
    }

    commentsStore.threads = extractCommentsFromData(data);
    applyProjectReactions(data.reactions || {});

    bump();
    return true;
  } catch {
    return false;
  }
}

/* ---------------- Stage toggles / panel ---------------- */
export const goPreview = () => {
  tattoo.stage = "preview";
  bump();
};
export const goDesign = () => {
  tattoo.stage = "design";
  bump();
};
export const openLeft = () => {
  tattoo.leftOpen = true;
  bump();
};
export const closeLeft = () => {
  tattoo.leftOpen = false;
  bump();
};
export const toggleLeft = () => {
  tattoo.leftOpen = !tattoo.leftOpen;
  bump();
};

/* ---------------- Dirty tracking (save-before-leave) ------------------- */
let _lastSavedSig = null;
let _dirtyBump = 0;

console.log("[autosave] export contains owner?", {
  top: tattoo.layers?.[0]?.name,
  source: tattoo.layers?.[0]?.source,
  ownerShopId: tattoo.layers?.[0]?.ownerShopId,
  ownerShopName: tattoo.layers?.[0]?.ownerShopName,
});

export function markDirty(tag = "manual") {
  _dirtyBump++;
  try {
    console.log(
      `[autosave] markDirty (${tag}) @ ${new Date().toLocaleTimeString()}`,
      { bump: _dirtyBump, layers: (tattoo.layers || []).length }
    );
  } catch {}
}

export function computeChangeSignature() {
  try {
    const layers = (tattoo.layers || []).map((l) => [
      l.id,
      l.name,
      !!l.url,
      l.visible,
      l.opacity,
      l.blend,
      l.edgeClip,
      l.sex,
      l.part,
      (l.partKeys || []).join("|"),
      l.transform?.tx,
      l.transform?.ty,
      l.transform?.scale,
      l.transform?.rot,
      // text edits (colour, font, etc.) change the image but not the
      // name/transform — include the settings so autosave notices.
      l.kind === "text" ? JSON.stringify(l.text || {}) : null,
    ]);

    const commentsCount = Object.values(commentsStore.threads || {}).reduce(
      (n, arr) => n + ((arr && arr.length) || 0),
      0
    );

    const globalBits = {
      skinColor: tattoo.skinColor,
      viewMode: tattoo.viewMode,
      parts: tattoo.parts,
      morphs: tattoo.morphs || {},
      poseWeights: tattoo.poseWeights || {},
      sex: tattoo.sex,
      viewState: vs
        ? {
            pos: vs.pos,
            target: vs.target,
            fov: vs.fov,
            zoom: vs.zoom,
            at: vs.at,
          }
        : null,

      // ✅ force dirty when markDirty() is called
      dirtyBump: _dirtyBump,
    };

    return JSON.stringify([layers.length, layers, commentsCount, globalBits]);
  } catch {
    return String(Math.random());
  }
}

if (typeof window !== "undefined") {
  window.tattoo = tattoo;
}

export function markSaved(tag = "manual") {
  _lastSavedSig = computeChangeSignature();
  try {
    console.log(
      `[autosave] markSaved (${tag}) @ ${new Date().toLocaleTimeString()}`,
      { layers: (tattoo.layers || []).length }
    );
  } catch {}
}

export function isDirty() {
  const cur = computeChangeSignature();
  return _lastSavedSig !== cur;
}

export function setRenderQuality(q) {
  if (tattoo.renderQuality === q) return; // ✅ no-op if unchanged
  tattoo.renderQuality = q;
  tattoo.renderQualityTick++;
}
